import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { AppConstants } from "../constants/constants";
import { Methods } from "../constants/MethodConstants";
import { formatDateToDDMMYYYY, getYearDifference } from "./date-and-time-format";

/**
 * Common input field regex
 */
export const passwordRegex =
  /^((?=.*[a-z])(?=.*[A-Z])(?=.*\d)|(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])|(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9])|(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]))([A-Za-z\d@#$%^&amp;*\-_+=[\]{}|\\:',?/`~&quot;();!]|\.(?!@)){8,16}$/;

export const usernameRegex = /^[a-zA-Z0-9]+[a-zA-Z0-9.'']*[-_a-zA-Z0-9.']*$/;
export const namesRegex = /^[a-zA-Z_. '-]+$/;
export const specialCharacter = /[^A-Za-z0-9-.'_ ]/;
export const nonSpecialCharRegex = /^[\ a-zA-Z0-9._'-]*$/;
export const nonSpecialCharAndEmailRegex = /^(?:[a-zA-Z0-9_'-. &:/]+|(?=.{2,100}$)[a-zA-Z0-9_'-]+(?:\.[a-zA-Z0-9_'-]+)*@[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*\.[a-zA-Z]{2,16})$/;
export const customSpecialRegEx = /^[a-zA-Z0-9 \.\_\-\'][a-zA-Z0-9 \.\_\-\']*$/;
export const resultSpecialCharRegEx = /^[a-zA-Z0-9\s\\\/\-%]+$/;
export const usernameOrEmailRegex = /^([a-zA-Z0-9]+[a-zA-Z0-9.'']*[-_a-zA-Z0-9.']*|(?=.{2,100}$)[a-zA-Z0-9_'-]+(?:\.[a-zA-Z0-9_'-]+)*@[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*\.[a-zA-Z]{2,16})$/;
/**
 *
 * Regex description
 * <, >, ", (, ), &, +, ', \": These characters are matched directly with their literal values inside the character class.
 * \\: This character is escaped with a backslash (\) to prevent it from being interpreted as an escape character.
 * %, \x00, \x0d, \x0a: These characters are matched using their hexadecimal values in the character class. Note that \x00 matches null bytes, while \x0d and \x0a match carriage return and line feed characters, respectively.
 * ./\\: These characters are matched directly with their literal values inside the character class. Note that . and / are included to disallow path alteration characters (../ and ..\).
 * added to these regex is it does not allow white spaces
 */
export const blockedCharacterRegex =
  /^(?!\s)(?!.*\s$)[^<>"&+\\\"%()\r\n\x00\x0d\x0a./\\]*$/;

/**
 * Regex description for Email
 * /^ start of the regex
 * [^.\s] - This block is to accept characters not presented on the list. Characters not allowed (which is on the list) are . and [space].
 * [\w\.-]* - This block is to accept any word character a-zA-Z0-9_. Adding special characters are . - _. Where _ is from \w.
 *            * denotes that the current blocks is NOT REQUIRED to have a match.
 * [^.] - This block is to accept characters not presented on the list. Character not allowed (which is on the list) is ..
 *        This block rule is before @ sign.
 * @ - The @ sign of the email.
 * [a-zA-Z\d-]+ - This block is to accept a-z, A-Z, \d is 0-9 digit and -.
 *                + denotes that the current block is REQUIRED to have a match.
 * (\.[a-zA-Z]{2,})+ - This block accept . as start of the rule, follow by a-z or A-Z with 2 or more in lenght.
 *                     This rule will be repeated on each . as rule for domain. E.g .com.uk, the rule will run twice for .com and for .uk
 *                     + denotes that the current block is REQUIRED to have a match.
 * $/ - end of regex
 */
export const emailRegex =
  /^(?=.{2,100}$)[a-zA-Z0-9_'-]+(?:\.[a-zA-Z0-9_'-]+)*@[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*\.[a-zA-Z]{2,16}$/;

/**
 * Clean form function
 * @param form: FormGroup
 */
export function formReset(form: FormGroup) {
  form.reset();
  Object.keys(form.controls).forEach((key) => {
    form.get(key).reset();
  });
}

export function confirmPasswordValidator(
  control: AbstractControl
): { [key: string]: any } | null {
  const source = control.get(AppConstants.PASSWORD);
  const target = control.get(AppConstants.CONFIRM_PASSWORD);

  if (source.value && target.value) {
    updateError(
      AppConstants.PASSWORD_MISMATCH,
      target,
      source.value === target.value ? Methods.DELETE : Methods.ADD
    );
  } else {
    const invalidControlValues = !(
      (target.value === null ? true : target.value.length == 0) &&
      (source.value === null ? true : source.value.length == 0)
    );

    updateError(
      AppConstants.PASSWORD_MISMATCH,
      target,
      invalidControlValues ? Methods.ADD : Methods.DELETE
    );
    updateError(
      AppConstants.REQUIRED,
      target,
      invalidControlValues ? Methods.DELETE : Methods.ADD
    );
  }

  return null;
}

export function confirmEmailValidator(
  control: AbstractControl
): { [key: string]: any } | null {
  const source1 = control.get(AppConstants.EMAIL);
  const source2 = control.get(AppConstants.NEW_EMAIL);
  const target = control.get(AppConstants.CONFIRM_EMAIL);

  const source = source1 ?? source2;

  if (source.value && target.value) {
    updateError(
      AppConstants.EMAIL_MISMATCH,
      target,
      source.value === target.value ? Methods.DELETE : Methods.ADD
    );
  } else {
    const invalidControlValues = !(
      (target.value === null || target.value === undefined
        ? true
        : target.value.length == 0) &&
      (source.value === null || source.value === undefined
        ? true
        : source.value.length == 0)
    );
    updateError(
      AppConstants.EMAIL_MISMATCH,
      target,
      invalidControlValues ? Methods.ADD : Methods.DELETE
    );
    updateError(
      AppConstants.REQUIRED,
      target,
      invalidControlValues ? Methods.DELETE : Methods.ADD
    );
  }

  return null;
}

export function confirmNewPasswordValidator(
  control: AbstractControl
): { [key: string]: any } | null {
  const source = control.get(AppConstants.NEW_PASSWORD);
  const target = control.get(AppConstants.CONFIRM_PASSWORD);

  const sourceValue = source.value;
  const targetValue = target.value;

  if (targetValue && targetValue !== sourceValue) {
    target.setErrors({ [AppConstants.PASSWORD_MISMATCH]: true });
  } else if (!targetValue && sourceValue && sourceValue !== targetValue) {
    target.setErrors({ [AppConstants.PASSWORD_MISMATCH]: true });
  } else if (target.hasError(AppConstants.PASSWORD_MISMATCH)) {
    const errorsCopy = { ...target.errors };
    delete errorsCopy[AppConstants.PASSWORD_MISMATCH];
    target.setErrors(Object.keys(errorsCopy).length === 0 ? null : errorsCopy);
  }

  return null;
}

export function confirmNewEmailValidator(
  control: AbstractControl
): { [key: string]: any } | null {
  const source = control.get(AppConstants.NEW_EMAIL);
  const target = control.get(AppConstants.CONFIRM_EMAIL);
  const sourceValue = source.value;
  const targetValue = target.value;

  if (targetValue && targetValue !== sourceValue) {
    target.setErrors({ [AppConstants.EMAIL_MISMATCH]: true });
  } else if (!targetValue && sourceValue && sourceValue !== targetValue) {
    target.setErrors({ [AppConstants.EMAIL_MISMATCH]: true });
  } else if (target.hasError(AppConstants.EMAIL_MISMATCH)) {
    const errors = { ...target.errors };
    delete errors[AppConstants.EMAIL_MISMATCH];
    target.setErrors(Object.keys(errors).length === 0 ? null : errors);
  }

  return null;
}

export function sameCurrentEmailValidator(
  control: AbstractControl
): { [key: string]: any } | null {
  const source = control.get(AppConstants.CURRENT_EMAIL);
  const target = control.get(AppConstants.NEW_EMAIL);

  if (control && target.value != null && source.value != null) {
    if (target.value.toLowerCase() === source.value.toLowerCase()) {
      target.setErrors({ [AppConstants.SAME_EMAIL]: true });
    } else if (target.hasError(AppConstants.SAME_EMAIL)) {
      delete target.errors[AppConstants.SAME_EMAIL];
      target.setErrors(
        Object.keys(target.errors).length === 0 ? null : target.errors
      );
    }
  }

  return null;
}

export function dateValidator(
  control: AbstractControl
): ValidationErrors | null {
  let value = control.value;

  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    value = formatDateToDDMMYYYY(value);
  } else if (
    typeof value === AppConstants.STRING_VALUE &&
    !isNaN(Date.parse(value))
  ) {
    value = formatDateToDDMMYYYY(new Date(value));
  } else return null;

  const [day, month, year] = value.split("/").map(Number);

  if (year < 2024) {
    return { dateInvalid: true };
  }

  const date = new Date(year, month - 1, day);

  const regex = /^\d{2}\/\d{2}\/\d{4}$/;

  if (!regex.test(value)) {
    return { dateInvalid: true };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date < today) {
    return { dateInvalid: true };
  }

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return { dateInvalid: true };
  }

  return null;
}

export function updateError(
  property: string,
  control: AbstractControl,
  method: Methods
): void {
  if (method === Methods.DELETE) {
    if (control.hasError(property)) {
      delete control.errors[property];
    }
  }

  if (method === Methods.ADD) {
    if (!control.hasError(property)) {
      let controlErrors = getErrors(control);
      const addError = { [property]: true };
      control.setErrors(
        controlErrors === null
          ? { [property]: true }
          : { ...controlErrors, ...addError }
      );
    }
  }

  if (control.errors) {
    if (Object.keys(control.errors).length === 0) {
      control.setErrors(null);
    }
  }
}

export function getErrors(control: AbstractControl): ValidationErrors {
  if (control.errors) {
    if (Object.keys(control.errors).length === 0) {
      return null;
    } else {
      return control.errors;
    }
  }
  return null;
}

export function getErrorLength(control: AbstractControl): number {
  return control?.errors ? Object.keys(control?.errors).length : 0;
}

export function atLeastOneCheckboxCheckedValidator(control: AbstractControl): ValidationErrors {
  const formGroup = control as FormGroup;
  const isAtLeastOneChecked = Object.values(formGroup.controls).some((control: AbstractControl) => control.value === true);

  return isAtLeastOneChecked ? null : { atLeastOneChecked: true };
}

export function validateDateRange(
  fromControlName: string,
  toControlName: string
): ValidatorFn {

  return (control: AbstractControl): { [key: string]: any } | null => {
    const dateFromControl = control.get(fromControlName);
    const dateToControl = control.get(toControlName);

    if (!dateFromControl || !dateToControl) {
      return null; // Skip validation if controls are not found
    }
    
    if (dateFromControl?.value && dateToControl?.value) {
      const fromDate = new Date(dateFromControl.value);
      const toDate = new Date(dateToControl.value);
      const yearDiff = getYearDifference(fromDate, toDate);
  
      if(yearDiff > 2) {
        dateFromControl.setErrors({dateExceeds: true});
        dateToControl.setErrors({dateExceeds: true});
      }
      else if (fromDate > toDate) {
        dateToControl.setErrors({dateInvalid: true});
      }
      else {
        dateFromControl.setErrors(null);
        dateToControl.setErrors(null);
      }
    }  
    else if (dateFromControl?.value || dateToControl?.value) {
      if(dateFromControl?.value && !dateToControl?.value &&
        dateFromControl.hasError(AppConstants.DATE_EXCEEDS)
      ) {
        dateFromControl.setErrors(null);
      }
      else if(!dateFromControl?.value && dateToControl?.value &&
        dateToControl.hasError(AppConstants.DATE_EXCEEDS)
      ) {
        dateToControl.setErrors(null);
      }
    }
    
    return null;
  }
}

//Typically for non-required date but triggered once field has been inputted
export function validateMissingDateRange(
  fromControlName: string,
  toControlName: string
): ValidatorFn {

  return (control: AbstractControl): { [key: string]: any } | null => {
    const dateFromControl = control.get(fromControlName);
    const dateToControl = control.get(toControlName);

    if (!dateFromControl || !dateToControl) {
      return null; // Exit early if controls don't exist
    }
    // Check if one field is filled but the other isn't
    else if (dateFromControl.value && !dateToControl.value) {
      dateToControl.setErrors({ required: true });
    } else if (!dateFromControl.value && dateToControl.value) {
      dateFromControl.setErrors({ required: true });
    }
    else if(!dateFromControl.value && !dateToControl.value) {
      dateFromControl.setErrors(null);
      dateToControl.setErrors(null);        
    }

    return null;
  };
}